import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import { quizData } from '@/data/quizData';
import { Home, CheckCircle2, XCircle, Clock } from 'lucide-react';
import SubmitScoreModal from '@/react-app/components/SubmitScoreModal';

interface QuizQuestion {
  question: string;
  answer: string;
  image?: string;
  options: string[];
}

export default function Quiz() {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [showSubmitScore, setShowSubmitScore] = useState(false);
  
  // Timer state
  const [elapsedTimeMs, setElapsedTimeMs] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const timerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);

  useEffect(() => {
    const quiz = quizData.find(q => q.quizID === quizId);
    if (!quiz) {
      navigate('/');
      return;
    }

    // Shuffle questions
    const shuffledQuestions = [...quiz.questions].sort(() => Math.random() - 0.5);

    // Get all answers from the quiz for multiple choice generation
    const allAnswers = quiz.questions.map(q => q.answer);

    // Create questions with randomized multiple choice options
    const questionsWithOptions = shuffledQuestions.map(q => {
      // Get 3 wrong answers (excluding the correct one)
      const wrongAnswers = allAnswers
        .filter(a => a !== q.answer)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3);

      // Combine correct answer with wrong answers and shuffle
      const options = [...wrongAnswers, q.answer].sort(() => Math.random() - 0.5);

      return {
        question: q.question,
        answer: q.answer,
        image: q.image,
        options
      };
    });

    setQuestions(questionsWithOptions);
    
    // Start timer when quiz loads
    setIsTimerRunning(true);
    startTimeRef.current = Date.now();
  }, [quizId, navigate]);

  // Timer effect
  useEffect(() => {
    if (isTimerRunning) {
      timerRef.current = window.setInterval(() => {
        setElapsedTimeMs(Date.now() - startTimeRef.current);
      }, 10); // Update every 10ms for millisecond precision
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isTimerRunning]);

  const handleAnswerSelect = (answer: string) => {
    if (showResult) return;
    
    setSelectedAnswer(answer);
    setShowResult(true);
    
    // Pause timer when answer is selected
    setIsTimerRunning(false);
    
    if (answer === questions[currentQuestionIndex].answer) {
      setScore(score + 1);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
      setShowResult(false);
      
      // Resume timer when next question starts
      setIsTimerRunning(true);
      startTimeRef.current = Date.now() - elapsedTimeMs;
    } else {
      setIsComplete(true);
      setShowSubmitScore(true);
    }
  };

  const handleRestart = () => {
    window.location.reload();
  };

  const formatTime = (ms: number): string => {
    const seconds = ms / 1000;
    return seconds.toFixed(2);
  };

  if (questions.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-blue-900 text-xl">Loading quiz...</div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const quiz = quizData.find(q => q.quizID === quizId);

  if (isComplete) {
    const percentage = Math.round((score / questions.length) * 100);
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="bg-gradient-to-br from-blue-900 to-blue-700 rounded-3xl p-12 text-center text-white shadow-2xl">
            <div className="mb-6">
              <CheckCircle2 className="w-20 h-20 mx-auto" />
            </div>
            <h1 className="text-4xl font-bold mb-4">Quiz Complete!</h1>
            <p className="text-6xl font-bold mb-6">{percentage}%</p>
            <p className="text-2xl mb-4">
              You got {score} out of {questions.length} questions correct
            </p>
            <p className="text-xl mb-8">
              Time: {formatTime(elapsedTimeMs)}s
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={handleRestart}
                className="px-8 py-3 bg-white text-blue-900 rounded-xl font-semibold hover:bg-blue-50 transition-colors"
              >
                Try Again
              </button>
              <Link
                to="/"
                className="px-8 py-3 bg-blue-800 text-white rounded-xl font-semibold hover:bg-blue-900 transition-colors flex items-center gap-2"
              >
                <Home className="w-5 h-5" />
                Return to Home
              </Link>
            </div>
          </div>
        </div>

        {quizId && (
          <SubmitScoreModal
            isOpen={showSubmitScore}
            onClose={() => setShowSubmitScore(false)}
            quizId={quizId}
            score={score}
            totalQuestions={questions.length}
            timeMs={elapsedTimeMs}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-blue-900">{quiz?.quizName}</h1>
            <p className="text-gray-600 mt-1">
              Question {currentQuestionIndex + 1} of {questions.length}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-xl border-2 border-blue-900">
              <Clock className="w-5 h-5 text-blue-900" />
              <span className="font-mono font-semibold text-blue-900">
                {formatTime(elapsedTimeMs)}s
              </span>
            </div>
            <Link
              to="/"
              className="flex items-center gap-2 px-4 py-2 text-blue-900 border-2 border-blue-900 rounded-xl hover:bg-blue-50 transition-colors font-semibold"
            >
              <Home className="w-5 h-5" />
              Return to Home
            </Link>
          </div>
        </div>

        <div className="bg-white border-2 border-blue-900 rounded-2xl p-8 shadow-lg">
          <div className="mb-8">
            <div className="w-full bg-blue-100 rounded-full h-3 mb-6">
              <div
                className="bg-gradient-to-r from-blue-900 to-blue-700 h-3 rounded-full transition-all duration-300"
                style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
              />
            </div>
            
            {currentQuestion.image && (
              <div className="mb-6 flex justify-center">
                <img
                  src={currentQuestion.image}
                  alt="Quiz question"
                  className="max-h-64 w-auto object-contain rounded-xl shadow-md"
                />
              </div>
            )}
            
            <h2 className="text-2xl font-semibold text-blue-900 mb-6">
              {currentQuestion.question}
            </h2>
          </div>

          <div className="space-y-3 mb-8">
            {currentQuestion.options.map((option, index) => {
              const isSelected = selectedAnswer === option;
              const isCorrect = option === currentQuestion.answer;
              const showAsCorrect = showResult && isCorrect;
              const showAsWrong = showResult && isSelected && !isCorrect;
              
              return (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(option)}
                  disabled={showResult}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 font-medium ${
                    showAsCorrect
                      ? 'border-green-500 bg-green-50'
                      : showAsWrong
                      ? 'border-red-500 bg-red-50'
                      : showResult
                      ? 'border-gray-200 bg-gray-50'
                      : isSelected
                      ? 'border-blue-900 bg-blue-50'
                      : 'border-blue-900 hover:bg-blue-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={
                      showAsCorrect
                        ? 'text-green-700'
                        : showAsWrong
                        ? 'text-red-700'
                        : 'text-blue-900'
                    }>
                      {option}
                    </span>
                    {showAsCorrect && (
                      <CheckCircle2 className="w-6 h-6 text-green-500" />
                    )}
                    {showAsWrong && (
                      <XCircle className="w-6 h-6 text-red-500" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex justify-end">
            {showResult && (
              <button
                onClick={handleNext}
                className="px-8 py-3 bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                {currentQuestionIndex < questions.length - 1 ? 'Next Question' : 'Finish Quiz'}
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 text-center text-gray-600">
          Score: {score} / {currentQuestionIndex + (showResult ? 1 : 0)}
        </div>
      </div>
    </div>
  );
}
